import * as React from 'react';
import {
  IconButton,
  TableRow,
  TableCell,
  Table,
  TableBody,
  useMediaQuery,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { fetchAllUsersAndRoles } from 'actions/UserAction/UserActions';
import AddButton from 'components/MUIDatatableAddButton/AddButton';
import EditIcon from '@mui/icons-material/Edit';
import GenericSpinner from 'components/GenericSpinner/GenericSpinner';
import GenericModal from 'components/GenericModal/GenericModal';
import CreateOrUpdateUser from 'components/CreateOrUpdateUser/CreateOrUpdateUser';
import MUIDataTableWrapper from 'components/MUIDataTableWrapper/MUIDataTableWrapper';

// Import Zustand store
import { useAppStore } from '../../stores';

interface UserAccount {
  userId: number;
  name: string;
  userName: string;
  roleName: string;
  active: boolean;
}

interface IUsersManagementProps {}

const UsersManagement: React.FC<IUsersManagementProps> = () => {
  const displayMobile = useMediaQuery('(max-width:959px)');

  // Zustand store
  const selectedUser = useAppStore((state) => state.user.selectedUserToEdit);
  const setSelectedUserToEdit = useAppStore((state) => state.setSelectedUserToEdit);
  const setRoles = useAppStore((state) => state.setRoles);

  const [editUserModalOpen, setEditUserModalOpen] = React.useState(false);

  const [userAccounts, setUserAccounts] = React.useState<UserAccount[]>([]);

  const [initialRequestPending, setInitialRequestPending] = React.useState(false);

  const loadUsers = () => {
    setInitialRequestPending(true);
    fetchAllUsersAndRoles()
      .then((response: unknown[]) => {
        if (response) {
          // set users
          if (response[0]) {
            const { data } = response[0] as { data: Array<{ user: { userId: number; firstName: string; lastName: string; username: string }; userRole: { roleName: string } | null; userRoleName: string; active: boolean }> };
            const formattedValues = data.map((u) => {
              return {
                userId: u.user.userId,
                name: u.user.firstName + ' ' + u.user.lastName,
                userName: u.user.username,
                roleName: u.userRole?.roleName ?? u.userRoleName,
                active: u.active,
              };
            });
            setUserAccounts(formattedValues);
          }
          // set roles
          if (response[1]) {
            const { data } = response[1] as { data: unknown[] };
            setRoles(data);
          }
        }
        setInitialRequestPending(false);
      })
      .catch((error) => {
        console.log(error);
        setInitialRequestPending(false);
      });
  };

  React.useEffect(() => {
    loadUsers();
  }, []);

  return (
    <Grid container direction={'column'}>
      <Grid size={{ xs: 12, md: 12 }}>
        <h2>User Management</h2>
      </Grid>
      <Grid size={{ xs: 12, md: 12 }} className="pt-3">
        {initialRequestPending ? (
          <Grid
            container
            direction="column"
            alignItems="center"
            justifyContent="center"
          >
            <Grid size={{ xs: 12, md: 12 }}>
              <GenericSpinner includeOverlay={false} padding={0} />
            </Grid>
            <Grid size={{ xs: 12, md: 12 }} className="pt-3">
              <span>Loading Users...</span>
            </Grid>
          </Grid>
        ) : (
          <MUIDataTableWrapper
            title={'Current Users'}
            data={userAccounts}
            setExpandableRowContent={(rowData: unknown[]) => {
              return (
                <TableRow>
                  <TableCell />
                  <TableCell colSpan={3}>
                    <Table>
                      <TableBody className="inner-tbody-expandable-row">
                        <TableRow>
                          <TableCell className="tbl-col-container">
                            <div className="tbl-col-title">Edit</div>
                            <div>
                              <IconButton
                                aria-label="edit"
                                onClick={() => {
                                  setSelectedUserToEdit({
                                    user: {
                                      ...selectedUser.user,
                                      userId: rowData[0] as number,
                                    },
                                    role: selectedUser.role,
                                    memberId: selectedUser.memberId,
                                    affPinNum: selectedUser.affPinNum,
                                  });
                                  setEditUserModalOpen(true);
                                }}
                                className="p-0"
                              >
                                <EditIcon />
                              </IconButton>
                            </div>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell align="left" className="tbl-col-container">
                            <div className="tbl-col-title">User Role</div>
                            <div>
                              {rowData[3] ? String(rowData[3]) : 'N/A'}
                            </div>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableCell>
                </TableRow>
              );
            }}
            columns={[
              {
                name: 'userId',
                label: 'User Id',
                options: {
                  display: 'false' as const,
                },
              },
              { name: 'name', label: 'Name' },
              {
                name: 'userName',
                label: 'User Name',
                options: {
                  customBodyRender: (value: string) => (
                    <span style={{ wordBreak: 'break-all' }}>{value}</span>
                  ),
                },
              },
              {
                name: 'roleName',
                label: 'User Role',
                options: {
                  display: displayMobile ? ('false' as const) : ('true' as const),
                  filterOptions: {
                    names: [...new Set([...userAccounts.map(u => u.roleName), 'None'])].sort(),
                  },
                },
              },
              {
                name: 'Edit',
                options: {
                  customBodyRender: (value: unknown, tableMeta: { rowData: unknown[] }) => {
                    const userId = tableMeta.rowData[0] as number;
                    const userAccount = userAccounts.find(ua => ua.userId === userId);
                    
                    return (
                      <IconButton
                        aria-label="edit"
                        onClick={() => {
                          setSelectedUserToEdit({
                            user: {
                              ...selectedUser.user,
                              userId: userId,
                            },
                            role: selectedUser.role,
                            memberId: selectedUser.memberId,
                            affPinNum: selectedUser.affPinNum,
                            Active: userAccount?.active || false,
                          });
                          setEditUserModalOpen(true);
                        }}
                        className="p-0"
                      >
                        <EditIcon />
                      </IconButton>
                    );
                  },
                  display: displayMobile ? ('false' as const) : ('true' as const),
                },
              },
            ]}
            options={{
              print: false,
              download: false,
              filter: true,
              viewColumns: false,
              rowsPerPageOptions: [5, 10, 15, 25],
              selectableRows: 'none',
              responsive: 'vertical',
              customToolbar: () => {
                return (
                  <AddButton
                    title={'Add User Account'}
                    handleClick={() => {
                      setSelectedUserToEdit({
                        user: { ...selectedUser.user, userId: 0 },
                        role: { roleId: 0, roleName: '' },
                        memberId: '',
                        affPinNum: '',
                      });
                      setEditUserModalOpen(true);
                    }}
                  />
                );
              },
            }}
          />
        )}
      </Grid>
      <GenericModal
        open={editUserModalOpen}
        contentWidth={45}
        content={<CreateOrUpdateUser closeParentModal={setEditUserModalOpen} reloadUsers={loadUsers} />}
        onClose={() => setEditUserModalOpen(false)}
        title={
          selectedUser.user.userId === 0
            ? 'Create New User'
            : 'Edit Selected User'
        }
      />
    </Grid>
  );
};

export default UsersManagement;
